import { foldReplaySse } from '@dunea/shared/streaming/replay-fold';

/**
 * Transport-level replay compaction — the CLIENT-side twin of studio's
 * fold-on-resume, applied ONLY to the resume GET (`/api/chat/:id/stream`).
 *
 * Reconnecting to an in-flight generation replays the whole buffered stream;
 * unfolded, useChat processes every per-token chunk at O(n²) cost and the
 * renderer freezes (the all-tabs lockup). Studio already folds server-side;
 * this layer makes the CLIENT robust on its own — a server regression, an old
 * deploy, or any future burst source cannot firehose the UI again.
 *
 * Safety properties:
 *  - NEVER applied to the live build POST stream — normal builds are untouched.
 *  - Applied to an already-folded stream it is a near-no-op (nothing to merge).
 *  - The burst window is bounded (BURST_MAX_MS); live chunks pass through raw.
 *  - A cut-off trailing block at the burst boundary is preserved and prepended
 *    to the next live chunk — no data loss.
 */
const BURST_IDLE_MS = 250;
const BURST_MAX_MS = 8_000;

/** True for the reconnect endpoint only. */
export function isResumeStreamUrl(url: unknown): boolean {
  const s = typeof url === 'string' ? url : url instanceof URL ? url.href : '';

  return /\/api\/chat\/[^/]+\/stream(\?|$)/.test(s);
}

/** Wrap a resume response body: fold the replay burst, then pass live data through. */
export function foldResumeBody(body: ReadableStream<Uint8Array>): ReadableStream<Uint8Array> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const encoder = new TextEncoder();

  return new ReadableStream<Uint8Array>({
    async start(controller) {
      const IDLE = Symbol('idle');

      // Cache the pending read across the phase boundary — racing a FRESH
      // read() after a timeout would queue a second read and drop a chunk.
      let pending: Promise<ReadableStreamReadResult<Uint8Array>> | null = null;
      const nextChunk = () => {
        pending ??= reader.read().then((r) => {
          pending = null;

          return r;
        });

        return pending;
      };

      try {
        let burstRaw = '';
        let streamDone = false;
        const burstStart = Date.now();

        // Phase 1 — collect the replay burst (reads separated by < idle gap).
        while (Date.now() - burstStart < BURST_MAX_MS) {
          let timer: ReturnType<typeof setTimeout> | undefined;
          const idle = new Promise<typeof IDLE>((resolve) => {
            timer = setTimeout(() => resolve(IDLE), BURST_IDLE_MS);
          });
          const raced = await Promise.race([nextChunk(), idle]);
          clearTimeout(timer);

          if (raced === IDLE) {
            break;
          }

          const { done, value } = raced as ReadableStreamReadResult<Uint8Array>;

          if (done) {
            streamDone = true;
            break;
          }

          burstRaw += decoder.decode(value, { stream: true });
        }

        const { folded, remainder } = foldReplaySse(burstRaw);

        if (folded) {
          controller.enqueue(encoder.encode(folded));
        }

        let carry = remainder;

        // Phase 2 — live passthrough.
        while (!streamDone) {
          const { done, value } = await nextChunk();

          if (done) {
            break;
          }

          const text = carry + decoder.decode(value, { stream: true });
          carry = '';
          controller.enqueue(encoder.encode(text));
        }

        if (carry) {
          controller.enqueue(encoder.encode(carry));
        }

        controller.close();
      } catch (err) {
        controller.error(err);
      }
    },
    cancel(reason) {
      return reader.cancel(reason);
    },
  });
}
